"use client";

import { useMutation } from "@tanstack/react-query";
import type { SignInOptions } from "next-auth/react";
import { SessionProvider, signIn, signOut, useSession } from "next-auth/react";
import { createContext, useCallback, useContext, useEffect } from "react";
import z from "zod";

import { setAuthTokenGetter, useGetMe } from "@workspace/api";
import { identifyUser } from "@workspace/tracking/track-event";

import { AUTH_CHANNEL, LOGIN_EVENT, LOGOUT_EVENT } from "./broadcast";
import type { IExtendedSession } from "./types/next-auth";

type UserData = Awaited<ReturnType<typeof useGetMe>>["data"];

const csrfTokenSchema = z.object({
  csrfToken: z.string(),
});

interface AuthContextValue {
  session: IExtendedSession | null;
  logout: () => Promise<void>;
  meInfo: UserData;
}

interface LoginPayload extends Omit<SignInOptions<false>, "redirect"> {
  identifier: string;
  password: string;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const AUTH_URL =
  process.env.NEXT_PUBLIC_AUTH_URL ?? process.env.REACT_APP_AUTH_URL;

const TIMEOUT_MS = 4000; // 4 seconds

interface AuthProviderProps {
  children: React.ReactNode;
  session: IExtendedSession | null;
}

const ExtendedSessionProvider = ({
  children,
}: Omit<AuthProviderProps, "session">) => {
  const { data: session, update } = useSession();
  const jwt = session?.user?.jwt ?? "";

  const { data: meInfo } = useGetMe({
    token: jwt,
    enabled: !!jwt,
  });

  useEffect(() => {
    setAuthTokenGetter(() => jwt);

    return () => setAuthTokenGetter(null);
  }, [jwt]);

  const buyer = meInfo?.userType?.buyer;
  const buyerId = buyer?.id;
  const buyerType = buyer?.buyerType;
  const accountRegion = buyer?.accountRegion;
  const country = buyer?.country?.key;

  useEffect(() => {
    if (!buyerId) return;

    identifyUser({
      userId: String(buyerId),
      buyerType,
      accountRegion,
      country,
    });
  }, [buyerId, buyerType, accountRegion, country]);

  useEffect(() => {
    const channel = new BroadcastChannel(AUTH_CHANNEL);
    channel.onmessage = (event) => {
      if (event.data === LOGOUT_EVENT || event.data === LOGIN_EVENT) {
        void update();
      }
    };
    return () => channel.close();
  }, [update]);

  const useLogout = async () => {
    try {
      await Logout();
      await update();
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        meInfo,
        logout: useLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const AuthProvider = ({ children, session }: AuthProviderProps) => {
  return (
    <SessionProvider session={session}>
      <ExtendedSessionProvider>{children}</ExtendedSessionProvider>
    </SessionProvider>
  );
};

export const Logout = async (): Promise<void> => {
  try {
    // @TODO(ds): Direct sale sets user data in localStorage, we need to clear it when user logs out
    localStorage.removeItem("userData");

    const csrfToken = await getCsrfToken();

    const res = await fetch(`${AUTH_URL}/api/auth/signout`, {
      signal: AbortSignal.timeout(TIMEOUT_MS),
      method: "post",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-Auth-Return-Redirect": "1",
      },
      body: new URLSearchParams({ csrfToken: csrfToken, callbackUrl: "/" }),
    });

    if (!res.ok) {
      console.error("Error logging out:", res.statusText);
      return;
    }

    await res.json();

    const channel = new BroadcastChannel(AUTH_CHANNEL);
    channel.postMessage(LOGOUT_EVENT);
    channel.close();
  } catch (error) {
    console.error("Error logging out:", error);
    return;
  }
};

const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

const loginRequest = async (payload: LoginPayload) => {
  const result = await signIn("credentials", { ...payload, redirect: false });

  if (result.ok) {
    const channel = new BroadcastChannel(AUTH_CHANNEL);
    channel.postMessage(LOGIN_EVENT);
    channel.close();
  }

  return result;
};

const useLogin = () => {
  return useMutation({ mutationFn: loginRequest });
};

const getCallbackUrl = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get("callbackUrl");
};

export async function getCsrfToken() {
  const res = await fetch(`${AUTH_URL}/api/auth/csrf`, {
    signal: AbortSignal.timeout(TIMEOUT_MS),
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) {
    console.error("Error fetching CSRF token:", res.statusText);
    return "";
  }

  const { csrfToken } = csrfTokenSchema.parse(await res.json());
  return csrfToken;
}

/**
 * Redirects the user to the new Auth app login page, saving the current URL for post-login redirect.
 * Sets a cookie 'auth_redirect' with the current page URL, then navigates to the auth app login.
 */
const redirectToAuthLogin = () => {
  const lang =
    localStorage.getItem("currentLanguage") === "en-US" ? "en" : "ar";

  const currentUrl = window.location.href;
  const loginUrl = `${AUTH_URL}/${lang}/auth/login?callbackUrl=${encodeURIComponent(currentUrl)}`;

  window.location.href = loginUrl;
};

/**
 * Hook to get and clear the lastPath
 */
export function useLastPath() {
  const getLastPath = useCallback((): string | null => {
    try {
      const cookies = document.cookie.split("; ");
      const lastPathCookie = cookies.find((cookie) =>
        cookie.startsWith("lastPath="),
      );

      if (lastPathCookie) {
        const value = lastPathCookie.split("=")[1];
        return value ? decodeURIComponent(value) : null;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  return { getLastPath };
}

export {
  AuthProvider,
  getCallbackUrl,
  loginRequest,
  redirectToAuthLogin,
  signIn,
  signOut,
  useAuth,
  useLogin,
  useSession,
};
